import React from "react";
import { useNavigate,useSearchParams } from "react-router-dom";
import Header from "../components/layout/header/Header";
import Footer from "../components/layout/footer/Footer";
import {
  AuditForm,
  AuditProtocol,
  AuditReport,
  AuditStepper,
} from "../components/layout/audit";
import { AuditReportDTO } from "../components/layout/audit/AuditReport";
import "../App.css";
import "../components/layout/audit/AuditPage.css";
import loaderIcon from "../assets/Icon.svg";
import { transformAuditResponse } from "../utils/transformAuditResponse";
import {
  startWebAudit,
  startSocialAudit,
  getAuditStatus,
} from "../api/Audit/auditApi";
import { downloadAuditReport } from "../api/download/downloadapi";
import Toast, { ToastType } from "../components/ui/toast/Toast";
import { Helmet } from "react-helmet-async";


const RING_RADIUS = 90;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
const MAX_POLL_FAILURES = 5; // consecutive network errors before giving up
const POLL_INTERVAL = 2500; // poll /audit/status every 2.5s
const MAX_POLL_DURATION = 3 * 60 * 1000; // safety net: stop after ~3 min

// Fallback copy keyed by the backend error `code`, used only when the API
// doesn't send a `message`. The API message is always preferred.
const FALLBACK_MESSAGES: Record<string, string> = {
  VALIDATION_ERROR: "Please check your details and try again.",
  AUDIT_ALREADY_CLAIMED: "This IP has already claimed its free audit.",
  FETCH_BLOCKED:
    "Deep analysis for this platform is being fine-tuned. Try a website audit or another platform — your free audit is still available.",
  FETCH_UNREACHABLE:
    "We couldn't reach that page this time. Make sure the link is correct and public, then try again.",
  AUDIT_TIMEOUT: "Our AI took a little longer than expected. Please run it again.",
  AUDIT_ERROR: "Our engine hit a brief snag. Please try again in a moment.",
  NOT_FOUND: "Audit not found.",
};

// Toast variant for each error `code` (spec §Error scenarios).
const toastTypeForCode = (code?: string): ToastType => {
  switch (code) {
    case "FETCH_BLOCKED":
    case "FETCH_UNREACHABLE":
    case "AUDIT_TIMEOUT":
      return "warning";
    case "AUDIT_ALREADY_CLAIMED":
      return "info";
    default:
      return "error"; // AUDIT_ERROR, VALIDATION_ERROR, NOT_FOUND, unknown
  }
};

// Resolve a backend { code, message } into toast content.
const resolveError = (
  code?: string,
  message?: string,
): { message: string; type: ToastType } => ({
  message:
    message ||
    (code ? FALLBACK_MESSAGES[code] : "") ||
    "Something went wrong. Please try again.",
  type: toastTypeForCode(code),
});

const AuditPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type") === "web" ? "web" : "social";
  const isWeb = type === "web";

  const [countryCode, setCountryCode] = React.useState("+91");
  const [toast, setToast] = React.useState<{
    message: string;
    type: ToastType;
  } | null>(null);
  const [status, setStatus] = React.useState<
    "idle" | "submitting" | "auditing" | "failed" | "done"
  >("idle");
  const [progress, setProgress] = React.useState(0);
  const [report, setReport] = React.useState<AuditReportDTO | null>(null);
  const [, setFailureMessage] = React.useState("");

  const [showDownloadModal, setShowDownloadModal] = React.useState(false);
  const [downloadEmail, setDownloadEmail] = React.useState(
    localStorage.getItem("auditEmail") || "",
  );
  const COMING_SOON_MODE = true;

const [showComingSoon, setShowComingSoon] = React.useState(false);
const [, setFailureCode] = React.useState("");
  const pollRef = React.useRef<NodeJS.Timeout | null>(null);
  const pollTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const failuresRef = React.useRef(0);
  // Last submitted form data, so "Try again" can re-run without re-typing.
  const lastAuditDataRef = React.useRef<Record<string, string> | null>(null);

  const isSubmitting = status === "submitting";
  const isAuditing = status === "auditing";
  const isLoading = isSubmitting || isAuditing;
const navigate = useNavigate();
  const clearPolling = React.useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);
const goToErrorPage = (
  code: string,
  message: string,
  title = "Audit couldn't be completed"
) => {
  navigate("/error", {
    replace: true,
    state: {
      code,
      title,
      message,
      auditType: type,
      auditData: lastAuditDataRef.current,
    },
  });
};
  const handleStartAudit = async (data: Record<string, string>) => {
    clearPolling();
    setToast(null);
    setProgress(0);
    setReport(null);
    setFailureMessage("");
    setFailureCode("");
    failuresRef.current = 0;
    lastAuditDataRef.current = data;
   

    const payload = {
      name: data.fullName,
      email: data.email,
      phone: `${data.countryCode}${data.phone}`,
      state: data.state,
      city: data.city,
      painPoint: data.painPoints,
      domain: data.websiteUrl,
    };

    const socialPayload = {
      name: data.fullName,
      email: data.email,
      phone: `${data.countryCode}${data.phone}`,
      state: data.state,
      city: data.city,
      painPoint: data.painPoints,
      // Send the raw input trimmed; the backend strips a leading @ and detects
      // full URLs, so we don't over-clean here (would break LinkedIn URLs).
      handle: data.handle?.trim(),
      platform: data.platform,
    };

    localStorage.setItem("auditEmail", data.email);

    try {
      const response = isWeb
        ? await startWebAudit(payload)
        : await startSocialAudit(socialPayload);

      if (!response?.token) {
        throw new Error("Invalid response from server");
      }

      // Persist the token: re-submitting from the same IP returns 429 for a
      // completed audit but works for a failed one.
      localStorage.setItem("auditToken", response.token);
if (COMING_SOON_MODE) {
  setShowComingSoon(true);
  setStatus("idle");
  return;
}

      setStatus("auditing");
      pollAudit(type, response.token);
    } catch (err: any) {
      // Start error — every body is { success:false, code, message }.
      // Stay on the form so the user can fix input / retry (no navigation).
      const errData = err?.response?.data;
      const { message, type: toastType } = resolveError(
        errData?.code,
        errData?.message,
      );
      setToast({ message, type: toastType });
      setStatus("idle");
      setProgress(0);
    }
  };
React.useEffect(() => {
  if (showComingSoon || showDownloadModal) {
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
  } else {
    document.body.style.overflow = "";
    document.documentElement.style.overflow = "";
  }

  return () => {
    document.body.style.overflow = "";
    document.documentElement.style.overflow = "";
  };
}, [showComingSoon, showDownloadModal]);
  const pollAudit = (auditType: "web" | "social", token: string) => {
    const poll = async () => {
      try {
        const result = await getAuditStatus(auditType, token);
        failuresRef.current = 0;

        // Still pending — keep the loader up and keep polling.
        if (!result?.ready) return;

        // ready === true is terminal (completed OR failed). Stop polling.
        clearPolling();

        if (result.status === "completed") {
          // Only a completed response carries the full audit data.
          const transformed = transformAuditResponse(result);
          sessionStorage.setItem("auditReport", JSON.stringify(transformed));

          setProgress(100);
          setReport(transformed as AuditReportDTO);

          // Let the ring animate to 100% before swapping to the report.
          window.setTimeout(() => setStatus("done"), 600);
        } else {
       const { message, type: toastType } = resolveError(
  result.code,
  result.message,
);

setToast({ message, type: toastType });

setProgress(0);

goToErrorPage(
  result.code || "UNKNOWN_ERROR",
  message,
  result.title || "Audit Failed"
);

return;
        }
      } catch (err: any) {
        const httpStatus = err?.response?.status;
        const errData = err?.response?.data;
        const code: string | undefined = errData?.code;

        // Wrong/expired token, or missing type/token — reset to the form.
        if (
          httpStatus === 404 ||
          httpStatus === 400 ||
          code === "NOT_FOUND" ||
          code === "VALIDATION_ERROR"
        ) {
          clearPolling();
          const { message } = resolveError(code, errData?.message);
          setToast({ message, type: "error" });
          setStatus("idle");
          setProgress(0);
          return;
        }

        // Transient network error — tolerate a few, then surface a failure.
        console.error(err);
        failuresRef.current += 1;
        if (failuresRef.current >= MAX_POLL_FAILURES) {
          clearPolling();
         const message =
  "We couldn't reach the server. Please check your connection and try again.";

setToast({ message, type: "error" });

goToErrorPage(
  "NETWORK_ERROR",
  message,
  "Network Error"
);

return;
        }
      }
    };

    poll(); // check immediately, then on an interval
    pollRef.current = setInterval(poll, POLL_INTERVAL);

    // Safety net: stop after the max duration and surface a timeout.
    pollTimeoutRef.current = setTimeout(() => {
      clearPolling();
      const { message, type: toastType } = resolveError("AUDIT_TIMEOUT");
      setToast({ message, type: toastType });
     setProgress(0);

goToErrorPage(
  "AUDIT_TIMEOUT",
  message,
  "Audit Timed Out"
);

return;
    }, MAX_POLL_DURATION);
  };



  // Whenever we switch screens (form → loader → report/failed), jump to the
  // top. Without this the loader/report opens scrolled to wherever the submit
  // button was. Instant (bypasses the global smooth-scroll) so there's no
  // visible scroll animation.
  React.useEffect(() => {
    if (status === "idle") return;
    const html = document.documentElement;
    const previous = html.style.scrollBehavior;
    html.style.scrollBehavior = "auto";
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
    requestAnimationFrame(() => {
      html.style.scrollBehavior = previous;
    });
  }, [status]);

  // Slowly animate the progress ring. It decelerates as it nears 90% and
  // holds there until the real result arrives, because the server can take a
  // while to finish.
  React.useEffect(() => {
    if (!isAuditing) return;

    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) return prev;
        const step = Math.max(0.15, (90 - prev) * 0.015);
        return Math.min(prev + step, 90);
      });
    }, 500);

    return () => clearInterval(interval);
  }, [isAuditing]);

  // Clean up the poll timers on unmount.
  React.useEffect(() => {
    return () => clearPolling();
  }, [clearPolling]);

  const handleDownloadLastReport = () => {
    setShowDownloadModal(true);
  };

  const handleDownload = async () => {
    try {
      if (!downloadEmail.trim()) {
        setToast({ message: "Please enter your email.", type: "error" });
        return;
      }

      await downloadAuditReport({
        type: isWeb ? "web" : "social",
        email: downloadEmail,
      });

      localStorage.setItem("auditEmail", downloadEmail);
      setShowDownloadModal(false);
      setToast({ message: "Report sent successfully.", type: "success" });
    } catch (err: any) {
      console.error(err);
      const errData = err?.response?.data;
      const { message, type: toastType } = resolveError(
        errData?.code,
        errData?.message,
      );
      setToast({ message, type: toastType });
    }
  };

  const ringDashOffset = RING_CIRCUMFERENCE * (1 - progress / 100);

  return (
    <div
      className={`page-shell ${
        status === "done" ? "page-shell--report" : "page-shell--audit"
      }`}
    >
      <Helmet>
        <title>{isWeb ? "Web Audit | Initialize Analysis" : "Social Audit | Initialize Analysis"}</title>
        <meta 
          name="description" 
          content={
            isWeb 
              ? "Analyze your domain and website presence to identify conversion growth levers and technical narrative gaps." 
              : "Evaluate your social footprint across platforms to unlock content growth levers and strategy adjustments."
          } 
        />
      </Helmet>
      <Header />

      {status === "done" && report ? (
        <main className="page-shell__content">
          <div className="audit-report-stepper">
            <AuditStepper activeStep={2} label="STEP 2: RESULT" />
          </div>
          <AuditReport report={report} />
        </main>
      ) : (
        <main className="page-shell__content audit-page-shell">
          <section
            className="audit-page-hero"
            aria-labelledby="audit-page-title"
          >
            <AuditStepper
              activeStep={1}
              label={isLoading ? "ANALYZING" : "STEP 1: DETAILS"}
            />
            <h1 id="audit-page-title" className="audit-page-title">
              <span className="audit-page-title-muted">Initialize Your</span>{" "}
              <span className="audit-page-title-accent">
                {isWeb ? "Web Audit" : "Social Audit"}
              </span>
            </h1>
            <p className="audit-page-description">
              We analyze your digital presence across platforms to identify
              growth levers and narrative gaps. Start by sharing your core
              identifiers.
            </p>
          </section>

          {isLoading ? (
            <div className="audit-loader" role="status" aria-live="polite">
              <div className="audit-loader__ring">
                <span className="audit-loader__badge">
                  {Math.round(progress)}%
                </span>
                <svg className="audit-loader__svg" viewBox="0 0 260 260">
                  <circle
                    className="audit-loader__outer"
                    cx="130"
                    cy="130"
                    r={RING_RADIUS + 10}
                  />
                  <circle
                    className="audit-loader__track"
                    cx="130"
                    cy="130"
                    r={RING_RADIUS}
                  />
                  <circle
                    className="audit-loader__progress"
                    cx="130"
                    cy="130"
                    r={RING_RADIUS}
                    strokeDasharray={RING_CIRCUMFERENCE}
                    strokeDashoffset={ringDashOffset}
                  />
                </svg>
                <div className="audit-loader__core">
                  <div className="audit-loader__core-inner">
                    <span className="audit-loader__core-icon">
                      <img src={loaderIcon} alt="" />
                    </span>
                  </div>
                </div>
              </div>

              <div className="audit-loader__status">
                <p className="audit-loader__title">Analyzing your presence…</p>
                <p className="audit-loader__subtitle">
                  Fetching signals and generating recommendations…
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="audit-page-grid">
                <AuditProtocol
                  countryCode={countryCode}
                  onDownloadLastReport={handleDownloadLastReport}
                />

                <AuditForm
                  type={type}
                  countryCode={countryCode}
                  setCountryCode={setCountryCode}
                  onSubmit={handleStartAudit}
                />
              </div>
            </>
          )}
        </main>
      )}

     {showDownloadModal &&
  (COMING_SOON_MODE ? (
    <div className="coming-soon-overlay">
      <div className="coming-soon-popup">
        <div className="coming-soon-popup__icon"></div>

        <h3>Reports Coming Soon</h3>

        <p>
          AI-powered audit reports are currently under development and will be
          available after launch. Once released, you'll be able to download
          your latest audit report from here.
        </p>

        <button
          type="button"
          onClick={() => setShowDownloadModal(false)}
        >
          OK
        </button>
      </div>
    </div>
  ) : (
    <div
      className="download-modal-overlay"
      onClick={() => setShowDownloadModal(false)}
    >
      <div
        className="download-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <h3>Download Last Report</h3>

        <input
          type="email"
          placeholder="Enter your email"
          value={downloadEmail}
          onChange={(e) => setDownloadEmail(e.target.value)}
          autoComplete="off"
          onKeyDown={(e) => {
            if (e.key === "Enter") handleDownload();
          }}
        />

        <div className="download-modal-actions">
          <button
            className="download-modal-cancel"
            onClick={() => setShowDownloadModal(false)}
          >
            Cancel
          </button>

          <button
            className="download-modal-primary"
            onClick={handleDownload}
          >
            Download Report
          </button>
        </div>
      </div>
    </div>
  ))}
      
{/* Coming Soon Modal */}
   {showComingSoon && (
  <div className="coming-soon-overlay">
    <div className="coming-soon-popup">
      <h3>Coming Soon</h3>

      <p>
        AI-powered audit reports are currently under development and will be
        available soon.
      </p>

      <button
        type="button"
        onClick={() => navigate("/")}
      >
        Go Back to Home Page
      </button>
    </div>
  </div>
)}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <Footer />
    </div>
  );
};

export default AuditPage;
